'use client';

import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { fetchAlarmSoundSettings, updateAlarmSoundSettings, type AlarmSoundSettings } from '@/shared/api/alarm-sound-settings-client';
import { alarmSoundSettingsAtom, alarmSoundSettingsLoadedAtom, alarmSoundSettingsPendingAtom, alarmSoundSettingsStoreOptions } from '@/stores/realtime/alarm-sound-settings-store';

let alarmSoundSettingsLoadPromise: ReturnType<typeof fetchAlarmSoundSettings> | null = null;

/**
 * 인증 만료 응답인지 확인합니다.
 */
function isUnauthorizedFailure(result: Awaited<ReturnType<typeof fetchAlarmSoundSettings>>) {
  return !result.ok && (result.status === 401 || result.error.code === 'UNAUTHORIZED');
}

/**
 * 알림음 설정 최초 조회 요청을 공유해 StrictMode 재실행 중 중복 요청을 방지합니다.
 */
function loadAlarmSoundSettingsOnce() {
  if (!alarmSoundSettingsLoadPromise) {
    alarmSoundSettingsLoadPromise = fetchAlarmSoundSettings().finally(() => {
      alarmSoundSettingsLoadPromise = null;
    });
  }

  return alarmSoundSettingsLoadPromise;
}

/**
 * SystemConfig 기반 브라우저 알림음 설정을 조회하고 저장합니다.
 */
export function useAlarmSoundSettings() {
  const [settings, setSettings] = useAtom(alarmSoundSettingsAtom, alarmSoundSettingsStoreOptions);
  const [isLoaded, setIsLoaded] = useAtom(alarmSoundSettingsLoadedAtom, alarmSoundSettingsStoreOptions);
  const [isPending, setIsPending] = useAtom(alarmSoundSettingsPendingAtom, alarmSoundSettingsStoreOptions);

  useEffect(() => {
    if (isLoaded) {
      return;
    }

    let isActive = true;

    async function loadSettings() {
      setIsPending(true);

      try {
        const result = await loadAlarmSoundSettingsOnce();
        if (!isActive) {
          return;
        }

        if (result.ok) {
          setSettings(result.data);
          setIsLoaded(true);
          return;
        }

        if (isUnauthorizedFailure(result)) {
          setIsLoaded(true);
          return;
        }

        console.error('[useAlarmSoundSettings] 알림음 설정 조회 실패:', result.error);
        setIsLoaded(true);
      } catch (error) {
        if (isActive) {
          console.error('[useAlarmSoundSettings] 알림음 설정 조회 예외:', error);
        }

        if (isActive) {
          setIsLoaded(true);
        }
      } finally {
        setIsPending(false);
      }
    }

    void loadSettings();

    return () => {
      isActive = false;
    };
  }, [isLoaded, setIsLoaded, setIsPending, setSettings]);

  const saveSettings = useCallback(
    async (nextSettings: Partial<AlarmSoundSettings>) => {
      const previousSettings = settings;
      const optimisticSettings = { ...settings, ...nextSettings };
      setSettings(optimisticSettings);
      setIsPending(true);

      try {
        const result = await updateAlarmSoundSettings(nextSettings);
        if (result.ok) {
          setSettings(result.data);
          setIsLoaded(true);
          return true;
        }

        if (isUnauthorizedFailure(result)) {
          setSettings(previousSettings);
          return false;
        }

        console.error('[useAlarmSoundSettings] 알림음 설정 저장 실패:', result.error);
        setSettings(previousSettings);
        return false;
      } catch (error) {
        console.error('[useAlarmSoundSettings] 알림음 설정 저장 예외:', error);
        setSettings(previousSettings);
        return false;
      } finally {
        setIsPending(false);
      }
    },
    [settings, setIsLoaded, setIsPending, setSettings]
  );

  const setEnabled = useCallback((enabled: boolean) => saveSettings({ enabled }), [saveSettings]);

  return {
    isLoaded,
    isPending,
    saveSettings,
    setEnabled,
    settings,
  };
}
